import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  X, Download, Upload, Loader2, CheckCircle2, AlertCircle,
  FileSpreadsheet, Users, Copy, Trash2,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const HEADERS = [
  'name', 'phone', 'email', 'address', 'pincode',
  'serviceType', 'plotArea', 'buildingType', 'facingDirection',
  'designPackage', 'requirement', 'budget',
];

const SAMPLE_ROWS = [
  {
    name: 'Rahul Sharma', phone: '9876543210', email: 'rahul@example.com',
    address: '12 Park Street, Kolkata', pincode: '700016',
    serviceType: 'building', plotArea: '1200', buildingType: 'Two Storied (G+1)',
    facingDirection: 'East', designPackage: 'advanced',
    requirement: 'Modern duplex with 3 BHK', budget: '35L',
  },
  {
    name: 'Priya Das', phone: '9876500001', email: 'priya@example.com',
    address: '45 Salt Lake, Kolkata', pincode: '700091',
    serviceType: 'interior', plotArea: '900', buildingType: '3BHK Flat',
    facingDirection: 'North', designPackage: 'premium',
    requirement: 'Full home interior', budget: '8L',
  },
];

const extractSerial = (jobId) => {
  if (!jobId) return 0;
  const m = String(jobId).match(/TEMP-(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
};

const normalizePhone = (p) => String(p || '').replace(/\D/g, '').slice(-10);

export default function BulkImportLeads({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [existingPhones, setExistingPhones] = useState(new Set());
  const [lastSerial, setLastSerial] = useState(0);
  const [result, setResult] = useState(null);

  const loadContext = async () => {
    try {
      const leads = await pb.collection('leads').getFullList({ fields: 'phone,jobId', requestKey: null });
      const phones = new Set();
      let maxSerial = 0;
      leads.forEach((l) => {
        const p = normalizePhone(l.phone);
        if (p) phones.add(p);
        const s = extractSerial(l.jobId);
        if (s > maxSerial) maxSerial = s;
      });
      setExistingPhones(phones);
      setLastSerial(maxSerial);
    } catch (err) { console.error('Context load fail:', err); }
  };

  const downloadSample = () => {
    const ws = XLSX.utils.json_to_sheet(SAMPLE_ROWS, { header: HEADERS });
    ws['!cols'] = HEADERS.map((h) => ({ wch: Math.max(14, h.length + 4) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    XLSX.writeFile(wb, 'housedesigns99-leads-sample.xlsx');
    toast.success('Sample sheet downloaded');
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setParsing(true);
    setResult(null);
    try {
      await loadContext();
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
      const normalized = data.map((r, i) => {
        const name = String(r.name || r.Name || '').trim();
        const phone = String(r.phone || r.Phone || '').trim();
        const normPhone = normalizePhone(phone);
        const st = String(r.serviceType || r.service_type || '').trim().toLowerCase();
        const obj = {
          _rowIdx: i + 2,
          name, phone, normPhone,
          email: String(r.email || '').trim(),
          address: String(r.address || '').trim(),
          pincode: String(r.pincode || '').trim(),
          serviceType: st === 'interior' ? 'interior' : (st ? 'building' : ''),
          plotArea: String(r.plotArea || '').trim(),
          buildingType: String(r.buildingType || '').trim(),
          facingDirection: String(r.facingDirection || '').trim(),
          designPackage: String(r.designPackage || '').trim(),
          requirement: String(r.requirement || '').trim(),
          budget: String(r.budget || '').trim(),
        };
        obj.errors = [];
        if (!obj.name) obj.errors.push('name required');
        if (!obj.phone) obj.errors.push('phone required');
        else if (obj.normPhone.length < 10) obj.errors.push('invalid phone');
        else if (existingPhones.has(obj.normPhone)) obj.errors.push('DUPLICATE');
        return obj;
      });
      setRows(normalized);
      const valid = normalized.filter((r) => r.errors.length === 0).length;
      toast.success(`Parsed ${normalized.length} rows · ${valid} valid`);
    } catch (err) {
      toast.error('Parse failed: ' + err.message);
      setRows([]);
    } finally { setParsing(false); }
  };

  const validRows = useMemo(() => rows.filter((r) => r.errors.length === 0), [rows]);

  const handleImport = async () => {
    if (validRows.length === 0) return;
    if (!window.confirm(`Import ${validRows.length} leads?`)) return;
    setImporting(true);
    setProgress({ done: 0, total: validRows.length });
    let created = 0, skipped = 0;
    const errors = [];
    let serial = lastSerial;
    for (const row of validRows) {
      serial += 1;
      try {
        await pb.collection('leads').create({
          name: row.name,
          phone: Number(row.normPhone) || null,
          email: row.email || undefined,
          address: row.address || undefined,
          pincode: row.pincode || undefined,
          serviceType: row.serviceType || 'building',
          plotArea: row.plotArea || undefined,
          buildingType: row.buildingType || undefined,
          facingDirection: row.facingDirection || undefined,
          designPackage: row.designPackage || undefined,
          requirement: row.requirement || undefined,
          budget: row.budget || undefined,
          status: 'Lead',
          jobId: `TEMP-${serial}`,
          source: 'bulk_import',
        });
        created++;
      } catch (err) {
        skipped++;
        errors.push({ row: row._rowIdx, name: row.name, error: err.message });
      }
      setProgress({ done: created + skipped, total: validRows.length });
    }
    setResult({ created, skipped, errors });
    setImporting(false);
    if (created > 0) { toast.success(`✅ ${created} leads imported`); onImported?.(); }
  };

  const reset = () => { setFile(null); setRows([]); setResult(null); setProgress({ done: 0, total: 0 }); };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-slate-900/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && !importing && onClose()}>
      <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 px-5 py-4 flex justify-between items-center text-white">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-bold">Bulk Import Leads</h2>
              <p className="text-xs text-white/80">Excel / CSV upload with auto Job ID</p>
            </div>
          </div>
          <button onClick={onClose} disabled={importing} className="hover:bg-white/20 rounded-lg p-1.5"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={downloadSample}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold transition text-sm">
              <Download className="w-4 h-4" /> Download Sample Sheet
            </button>
            <label className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition text-sm font-semibold ${file ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-600'}`}>
              <Upload className="w-4 h-4" />
              {file ? file.name.slice(0, 24) : 'Choose Excel / CSV'}
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} disabled={parsing || importing} />
            </label>
          </div>

          {rows.length === 0 && !parsing && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800">
              <div className="font-bold mb-1.5">📋 How it works</div>
              <ul className="space-y-1 pl-4 list-disc">
                <li>Download Sample Sheet first</li>
                <li>Fill data, keep header row</li>
                <li>Save as .xlsx or .csv, upload here</li>
                <li>Job IDs auto-generate from last serial</li>
                <li><b>Duplicate phone numbers skipped automatically</b></li>
              </ul>
            </div>
          )}

          {parsing && (
            <div className="flex items-center justify-center py-10 gap-3 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" /> <span>Parsing file…</span>
            </div>
          )}

          {rows.length > 0 && !result && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs">
                <span className="font-bold text-slate-700">{rows.length} total</span>
                <span className="text-emerald-700 font-semibold">✅ {validRows.length} valid</span>
                {rows.filter((r) => r.errors.includes('DUPLICATE')).length > 0 && (
                  <span className="text-amber-700 font-semibold">⚠️ {rows.filter((r) => r.errors.includes('DUPLICATE')).length} dup</span>
                )}
                {rows.filter((r) => r.errors.length && !r.errors.includes('DUPLICATE')).length > 0 && (
                  <span className="text-red-700 font-semibold">❌ {rows.filter((r) => r.errors.length && !r.errors.includes('DUPLICATE')).length} invalid</span>
                )}
                <button onClick={reset} className="ml-auto text-slate-500 hover:text-red-600 flex items-center gap-1 font-medium">
                  <Trash2 className="w-3.5 h-3.5" /> Clear
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-white sticky top-0 border-b border-slate-200">
                    <tr>
                      <th className="p-2 text-left w-12">Row</th>
                      <th className="p-2 text-left">Name</th>
                      <th className="p-2 text-left">Phone</th>
                      <th className="p-2 text-left">Service</th>
                      <th className="p-2 text-left w-24">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((r, i) => {
                      const ok = r.errors.length === 0;
                      const dup = r.errors.includes('DUPLICATE');
                      return (
                        <tr key={i} className={ok ? '' : dup ? 'bg-amber-50/50' : 'bg-red-50/50'}>
                          <td className="p-2 text-slate-400 font-mono">{r._rowIdx}</td>
                          <td className="p-2 font-medium">{r.name || <span className="text-red-500 italic">—</span>}</td>
                          <td className="p-2 font-mono">{r.phone || <span className="text-red-500 italic">—</span>}</td>
                          <td className="p-2 capitalize">{r.serviceType || '—'}</td>
                          <td className="p-2">
                            {ok && <span className="text-emerald-700 font-semibold">✓ Ready</span>}
                            {dup && <span className="text-amber-700 font-semibold">⚠ Duplicate</span>}
                            {!ok && !dup && <span className="text-red-700 font-semibold">✗ {r.errors[0]}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {importing && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <div className="flex justify-between mb-2 text-sm font-semibold text-indigo-800">
                <span>Importing…</span><span>{progress.done} / {progress.total}</span>
              </div>
              <div className="w-full bg-indigo-200 rounded-full h-2 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 h-full transition-all"
                  style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
              </div>
            </div>
          )}

          {result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm">
              <div className="flex items-center gap-2 font-bold text-emerald-800 mb-2">
                <CheckCircle2 className="w-5 h-5" /> Import complete
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-white rounded-lg p-3 border border-emerald-100">
                  <div className="text-[10px] uppercase font-bold text-slate-500">Created</div>
                  <div className="text-2xl font-black text-emerald-700">{result.created}</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-red-100">
                  <div className="text-[10px] uppercase font-bold text-slate-500">Failed</div>
                  <div className="text-2xl font-black text-red-700">{result.skipped}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-3 flex justify-between items-center gap-3 bg-slate-50">
          <div className="text-xs text-slate-500">
            {rows.length > 0 && !result && <><Users className="w-3.5 h-3.5 inline mr-1" />{validRows.length} will be imported</>}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={importing}
              className="px-4 py-2 text-sm font-semibold rounded-xl border border-slate-200 hover:bg-slate-100 disabled:opacity-50">
              {result ? 'Close' : 'Cancel'}
            </button>
            {rows.length > 0 && !result && (
              <button onClick={handleImport} disabled={importing || validRows.length === 0}
                className="px-5 py-2 text-sm font-bold rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg disabled:opacity-50 flex items-center gap-2">
                {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <><Upload className="w-4 h-4" /> Import {validRows.length}</>}
              </button>
            )}
            {result && (
              <button onClick={reset}
                className="px-5 py-2 text-sm font-bold rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg flex items-center gap-2">
                <Upload className="w-4 h-4" /> Import Another
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
